import { Link } from "@tanstack/react-router";
import { CaseStudyTOC } from "@/components/caseStudy/CaseStudyTOC";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { MetricCard } from "@/components/metrics/MetricCard";
import { getProject } from "@/content/portfolio";
import { LOCATIONS, LocationPage } from "@/content/locations";
import { TOOLS } from "@/content/tools";
import { serviceVariantKey } from "@/content/imagery";
import { Picture } from "@/components/media/Picture";
import { BrandFigure, figuresForSlug } from "@/components/media/BrandFigure";
import { relatedBlogPosts, relatedLocations } from "@/lib/relatedGuides";
import { Breadcrumbs } from "@/components/shared/Breadcrumbs";
import { SiloDiscovery } from "@/components/shared/SiloDiscovery";
import type { ServicePage } from "@/content/services";
import type { BlogPost } from "@/content/blog";
import { siteConfig } from "@/config/site";


// Service category → tool categories relevant to that discipline. Powers the
// "Free tools" strip so every service page sends 3 contextual links to /tools/*.
const SERVICE_CAT_TO_TOOL_CATS: Record<string, Array<string>> = {
  "AI Search": ["AI Search", "Content", "On-Page"],
  Local: ["Local", "On-Page", "Technical"],
  Technical: ["Technical", "On-Page", "Content"],
  Content: ["Content", "On-Page", "AI Search"],
  Analytics: ["Technical", "On-Page", "Content"],
  Design: ["Technical", "On-Page", "Content"],
};

export function ServicePageTemplate({ service }: { service: ServicePage }) {
  const toc = [
    { id: "overview", label: "01 · Overview" },
    { id: "philosophy", label: "02 · Philosophy" },
    { id: "pillars", label: "03 · Pillars" },
    { id: "framework", label: "04 · Framework" },
    { id: "deliverables", label: "05 · What's included" },
    { id: "timeline", label: "06 · Timeline" },
    { id: "investment", label: "07 · Investment" },
    { id: "fit", label: "08 · Fit" },
    ...(service.relatedCaseStudies.length ? [{ id: "proof", label: "09 · Proof" }] : []),
    { id: "faq", label: `${service.relatedCaseStudies.length ? "10" : "09"} · FAQ` },
  ];

  const proofProjects = service.relatedCaseStudies
    .map(getProject)
    .filter(Boolean) as NonNullable<ReturnType<typeof getProject>>[];

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-70"
            style={{ background: "var(--gradient-hero)" }}
          />
          <div className="container-page relative pt-10 pb-12 md:pt-14 md:pb-16">
            <Breadcrumbs 
              items={[
                { name: "Services", path: "/services/" },
                { name: service.title, path: `/services/${service.slug}/` }
              ]} 
            />
            <div className="mt-8 grid lg:grid-cols-[1.4fr_1fr] gap-12 items-end">
              <div>
                <EyebrowLabel>{service.eyebrow} · {service.category}</EyebrowLabel>
                <h1 className="mt-5 font-display text-5xl md:text-7xl leading-[1.02] text-balance">
                  {service.hero.headline}
                </h1>
                <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
                  {service.hero.deck}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/contact/"
                    search={{ location: "", service: service.slug }}
                    className="inline-flex rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
                  >
                    Book a strategy call →
                  </Link>
                  <a
                    href="#investment"
                    className="inline-flex rounded-full border border-border-strong px-6 py-3 text-sm hover:border-primary hover:text-primary"
                  >
                    See investment
                  </a>
                </div>
              </div>
              <aside className="space-y-4">
                <div className="rounded-2xl overflow-hidden border border-border bg-surface-2">
                  <Picture
                    variantKey={serviceVariantKey(service.slug)}
                    alt=""
                    width={1024}
                    height={640}
                    priority
                    sizes="(min-width: 1024px) 400px, 100vw"
                  />
                </div>
                <div className="surface-card p-6">
                  <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                    At a glance
                  </div>
                  <ul className="mt-4 space-y-3">
                    {service.tldr.map((t) => (
                      <li key={t} className="flex items-start gap-3 text-sm">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* BODY: TOC + article */}
        <section className="container-page py-16 grid gap-12 lg:grid-cols-[220px_1fr] items-start">
          <CaseStudyTOC items={toc} />

          <article className="max-w-3xl min-w-0">
            {/* Overview */}
            <section id="overview" className="scroll-mt-24">
              <EyebrowLabel>Overview</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl leading-tight">
                Why {service.title.toLowerCase()} matters right now.
              </h2>
              <div className="mt-6 space-y-5">
                {service.overview.map((p, i) => (
                  <p key={i} className="text-base md:text-lg leading-relaxed text-foreground/90">
                    {p}
                  </p>
                ))}
              </div>
              <BrandFigure
                className="mt-8"
                kind={figuresForSlug(service.slug, 3)[0]}
                title={`${service.title}: what compounding progress looks like`}
              />
            </section>

            {/* Philosophy */}
            <section id="philosophy" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>Our philosophy</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl leading-tight text-balance">
                {service.philosophy.heading}
              </h2>
              <div className="mt-6 space-y-5">
                {service.philosophy.body.map((p, i) => (
                  <p key={i} className="text-base md:text-lg leading-relaxed text-foreground/90">
                    {p}
                  </p>
                ))}
              </div>
            </section>

            {/* Pillars */}
            <section id="pillars" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>The pillars</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                The disciplines that make this work.
              </h2>
              <div className="mt-8 space-y-6">
                {service.pillars.map((pil, i) => (
                  <div key={pil.title} className="surface-card p-6 md:p-7">
                    <div className="flex items-baseline gap-4">
                      <div className="font-display text-2xl text-primary">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <h3 className="font-display text-xl md:text-2xl">{pil.title}</h3>
                    </div>
                    <p className="mt-3 text-foreground/90 leading-relaxed">{pil.body}</p>
                    <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                      {pil.outputs.map((o) => (
                        <li key={o} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                          <span>{o}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Framework */}
            <section id="framework" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>{service.framework.name}</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                Our proprietary approach.
              </h2>
              <div className="mt-6 space-y-5">
                {service.framework.body.map((p, i) => (
                  <p key={i} className="text-base md:text-lg leading-relaxed text-foreground/90">
                    {p}
                  </p>
                ))}
              </div>
              <ol className="mt-8 grid gap-4 sm:grid-cols-2">
                {service.framework.steps.map((s, i) => (
                  <li key={s.label} className="surface-card p-5">
                    <div className="text-[0.7rem] uppercase tracking-widest text-primary">
                      Step {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="mt-2 font-display text-xl">{s.label}</div>
                    <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
                  </li>
                ))}
              </ol>
              <BrandFigure
                className="mt-8"
                kind={figuresForSlug(service.slug, 3)[1]}
                title={`${service.framework.name} — how the stages connect`}
              />
            </section>

            {/* Deliverables */}
            <section id="deliverables" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>What's included</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                Deliverables, by cadence.
              </h2>
              <div className="mt-8 grid gap-6 md:grid-cols-3">
                {service.deliverables.map((c) => (
                  <div key={c.cadence} className="surface-card p-6">
                    <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                      {c.cadence}
                    </div>
                    <ul className="mt-4 space-y-2.5">
                      {c.items.map((it) => (
                        <li key={it} className="flex items-start gap-2 text-sm">
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Timeline */}
            <section id="timeline" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>Timeline</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                A staged roadmap.
              </h2>
              <ol className="mt-8 relative border-l border-border pl-6 space-y-8">
                {service.timeline.map((phase) => (
                  <li key={phase.phase}>
                    <div className="absolute -left-[7px] h-3 w-3 rounded-full bg-primary" />
                    <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                      {phase.when}
                    </div>
                    <h3 className="mt-1 font-display text-xl">{phase.phase}</h3>
                    <ul className="mt-3 space-y-2">
                      {phase.work.map((w) => (
                        <li key={w} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                          <span>{w}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>
              <BrandFigure
                className="mt-8"
                kind={figuresForSlug(service.slug, 3)[2]}
                title={`Typical ${service.title.toLowerCase()} output curve across the engagement`}
              />
            </section>

            {/* Investment */}
            <section id="investment" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>Investment</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                Transparent pricing.
              </h2>
              <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
                All engagements include a 30-day exit clause. If we're not the right fit, you keep every asset produced.
              </p>
              <div className="mt-8 grid gap-6 md:grid-cols-3">
                {service.investment.map((tier, i) => (
                  <div
                    key={tier.tier}
                    className={`surface-card p-6 flex flex-col ${
                      i === 1 ? "ring-1 ring-primary/40" : ""
                    }`}
                  >
                    <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                      {tier.tier}
                    </div>
                    <div className="mt-3 font-display text-3xl">{tier.price}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{tier.cadence}</div>
                    <ul className="mt-5 space-y-2 flex-1">
                      {tier.includes.map((inc) => (
                        <li key={inc} className="flex items-start gap-2 text-sm">
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                          <span>{inc}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6 pt-4 border-t border-border text-xs text-muted-foreground">
                      Best for · <span className="text-foreground">{tier.best}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Fit */}
            <section id="fit" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>Is this for you?</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                We turn away work that isn't a fit.
              </h2>
              <div className="mt-8 grid gap-6 md:grid-cols-2">
                <div className="surface-card p-6">
                  <div className="text-primary text-xs uppercase tracking-widest">This is for you if</div>
                  <ul className="mt-4 space-y-2.5">
                    {service.fitFor.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="surface-card p-6">
                  <div className="text-muted-foreground text-xs uppercase tracking-widest">
                    Not a fit if
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {service.notFor.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-muted-foreground" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Proof */}
            {proofProjects.length > 0 && (
              <section id="proof" className="scroll-mt-24 mt-16 border-t border-border pt-12">
                <EyebrowLabel>Proof</EyebrowLabel>
                <h2 className="mt-4 font-display text-3xl md:text-4xl">
                  Engagements running this discipline.
                </h2>
                <div className="mt-8 grid gap-6">
                  {proofProjects.map((p) => (
                    <Link
                      key={p.slug}
                      to="/portfolio/$slug/"
                      params={{ slug: p.slug }}
                      className="surface-card p-6 grid gap-4 sm:grid-cols-[1fr_auto] items-center hover:-translate-y-0.5 transition-transform"
                    >
                      <div>
                        <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                          {p.industry} · {p.window}
                        </div>
                        <div className="mt-1 font-display text-xl">{p.client}</div>
                        <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                          {p.tagline}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-3xl text-primary">
                          {p.metrics[0].value}
                        </div>
                        <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">
                          {p.metrics[0].label}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Silo reinforcement */}
            <SiloDiscovery 
              eyebrow="Service connectivity"
              heading={`Where ${service.title} fits in your growth strategy.`}
              intro={`${service.title} is a specialized discipline within our full-stack search framework. For most Austin businesses, it is combined with local visibility and AI-search preparation to capture the entire buyer journey.`}
              links={[
                {
                  to: "/services/seo/",
                  anchor: "Full-stack SEO Agency programmes",
                  note: "The parent framework that carries all specialized search work."
                },
                {
                  to: "/industries/",
                  anchor: "Vertical-specific search playbooks",
                  note: "How we adapt this service for your specific industry economics."
                },
                {
                  to: `/seo-agency-${siteConfig.contact.address.city.toLowerCase()}/`,
                  anchor: `${siteConfig.contact.address.city} HQ & local strategy`,
                  note: "Where the team, the methodology, and the case studies originate."
                },
                {
                  to: "/contact/",
                  anchor: "Book a Strategy Call",
                  note: "Discuss how to map this service to your specific business goals."
                }
              ]}
            />

            {/* FAQ */}
            <section id="faq" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>FAQ</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                Questions we get on every call.
              </h2>
              <div className="mt-8 space-y-3">
                {service.faqs.map((f) => (
                  <details
                    key={f.q}
                    className="surface-card p-5 group open:ring-1 open:ring-primary/30"
                  >
                    <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                      <span className="font-medium text-foreground">{f.q}</span>
                      <span className="text-primary text-xl leading-none transition-transform group-open:rotate-45">
                        +
                      </span>
                    </summary>
                    <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
                      {f.a}
                    </p>
                  </details>
                ))}
              </div>
            </section>

            {/* CTA */}
            <section className="mt-16">
              <div className="surface-card p-8 md:p-10">
                <EyebrowLabel>Next step</EyebrowLabel>
                <h2 className="mt-3 font-display text-3xl md:text-4xl">
                  Ready to run {service.title.toLowerCase()} as a system?
                </h2>
                <p className="mt-3 text-muted-foreground max-w-xl">
                  Book a free strategy call. We'll audit your current footprint and tell
                  you honestly whether we're the right fit.
                </p>
                <Link
                  to="/contact/"
                  search={{ location: "", service: service.slug }}
                  className="mt-6 inline-flex rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
                >
                  Book a strategy call →
                </Link>
              </div>
            </section>
          </article>
        </section>

        {/* Related guides — deep-link to blog + location pages for topical context */}
        {(() => {
          const posts = relatedBlogPosts(service);
          const locs = relatedLocations(service);

          if (!posts.length && !locs.length) return null;
          return (
            <section className="container-page py-16 border-t border-border" aria-labelledby="svc-guides">
              <EyebrowLabel>Related guides & local coverage</EyebrowLabel>
              <h2 id="svc-guides" className="mt-3 font-display text-2xl md:text-3xl">
                Go deeper on {service.title.toLowerCase()}.
              </h2>
              <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
                Field-notes from real engagements, plus the Texas markets where we
                deploy this discipline most often.
              </p>
              <div className="mt-4">
                <Link
                  to="/services/$slug/guides/"
                  params={{ slug: service.slug }}
                  search={{ tag: "", page: 1 }}
                  className="text-sm text-primary story-link"
                >
                  Open the full {service.title} guides hub →
                </Link>
              </div>



              {posts.length > 0 && (
                <div className="mt-8">
                  <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                    From the field notes
                  </div>
                  <div className="mt-4 grid gap-6 md:grid-cols-3">
                    {posts.map((p: BlogPost) => (
                      <Link
                        key={p.slug}
                        to={"/blog/$slug/" as any}    
                        params={{ slug: p.slug } as any}
                        className="surface-card p-6 flex flex-col hover:-translate-y-1 transition-transform"
                      >
                        <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                          {p.category} · {p.readingMinutes} min
                        </div>
                        <div className="mt-2 font-display text-lg leading-snug">
                          {p.title}
                        </div>
                        <p className="mt-2 text-sm text-muted-foreground line-clamp-3 flex-1">
                          {p.dek}
                        </p>
                        <div className="mt-4 text-xs text-primary">Read guide →</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {locs.length > 0 && (
                <div className="mt-10">
                  <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                    Where we run this
                  </div>
                  <div className="mt-4 grid gap-4 sm:grid-cols-3">
                    {locs.map((l: LocationPage) => (
                      <Link
                        key={l.slug}
                        to={"/seo-agency-{$slug}" as any}
                        params={{ slug: l.slug } as any}
                        className="surface-card p-5 hover:-translate-y-0.5 transition-transform"
                      >
                        <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                          {l.region}
                        </div>
                        <div className="mt-1 font-display text-base">
                          {service.title} — {l.city}, TX
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground line-clamp-2">
                          {l.hero.deck}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </section>
          );
        })()}

        {/* Related services */}
        {service.related.length > 0 && (
          <section className="container-page py-16 border-t border-border">
            <EyebrowLabel>Related disciplines</EyebrowLabel>
            <h3 className="mt-3 font-display text-2xl md:text-3xl">
              Often paired with {service.title.toLowerCase()}
            </h3>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {service.related.map((slug) => {
                if (slug === "seo") {
                  return (
                    <Link
                      key={slug}
                      to="/services/seo/"
                      className="surface-card p-6 hover:-translate-y-1 transition-transform"
                    >
                      <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                        Search · Flagship
                      </div>
                      <h4 className="mt-2 font-display text-xl">SEO</h4>
                      <p className="mt-2 text-sm text-muted-foreground">
                        The integrated SEO programme — technical, content, authority, local, and AEO/GEO.
                      </p>
                    </Link>
                  );
                }
                return (
                  <Link
                    key={slug}
                    to="/services/$slug/"
                    params={{ slug }}
                    className="surface-card p-6 hover:-translate-y-1 transition-transform"
                  >
                    <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                      Discipline
                    </div>
                    <h4 className="mt-2 font-display text-xl">
                      {slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    </h4>
                    <p className="mt-2 text-sm text-muted-foreground">
                      See how this pairs with {service.title.toLowerCase()}.
                    </p>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Serving these Texas locations — internal linking to city pages */}
        <section
          className="container-page py-16 border-t border-border"
          aria-labelledby="svc-cities"
        >
          <EyebrowLabel>Serving Austin & Central Texas</EyebrowLabel>
          <h2 id="svc-cities" className="mt-4 font-display text-3xl md:text-4xl">
            {service.title} — city coverage.
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            We deploy this discipline for operators across the Austin metro and
            broader Texas markets. Pick your city for local context, ZIP-level
            coverage, and neighbourhood entities.
          </p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {LOCATIONS.slice(0, 12).map((l) => (
              <Link
                key={l.slug}
                    to="/seo-agency-{$slug}/"
                    params={{ slug: l.slug }}
                className="surface-card p-4 hover:-translate-y-0.5 transition-transform"
              >
                <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                  {l.region}
                </div>
                <div className="mt-1 font-display text-base">
                  {service.title} — {l.city}, TX
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Free tools — send contextual links into /tools/* */}
        {(() => {
          const wantCats = SERVICE_CAT_TO_TOOL_CATS[service.category] ?? ["On-Page", "Technical", "Content"];
          // Rotate per service slug so tool inbounds spread evenly across /tools/*
          const seed = service.slug.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
          const picked: typeof TOOLS = [];
          for (const cat of wantCats) {
            const pool = TOOLS.filter((x) => x.category === cat && !picked.includes(x));
            if (pool.length) picked.push(pool[seed % pool.length]);
            if (picked.length >= 3) break;
          }
          if (picked.length < 3) {
            const rest = TOOLS.filter((t) => !picked.includes(t));
            for (let i = 0; i < rest.length && picked.length < 3; i++) {
              picked.push(rest[(seed + i) % rest.length]);
            }
          }

          return (
            <section className="container-page py-16 border-t border-border" aria-labelledby="svc-tools">
              <EyebrowLabel>Free tools</EyebrowLabel>
              <h2 id="svc-tools" className="mt-4 font-display text-3xl md:text-4xl">
                Try the tools our {service.title.toLowerCase()} team uses.
              </h2>
              <div className="mt-8 grid gap-4 md:grid-cols-3">
                {picked.map((t) => (
                  <Link
                    key={t.slug}
                    to="/tools/$slug/"
                    params={{ slug: t.slug }}
                    className="surface-card p-6 hover:-translate-y-1 transition-transform"
                  >
                    <div className="text-[0.65rem] uppercase tracking-widest text-primary">{t.category}</div>
                    <div className="mt-2 font-display text-lg">{t.name}</div>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{t.tagline}</p>
                  </Link>
                ))}
              </div>
            </section>
          );
        })()}

        {/* Silo Discovery */}
        <SiloDiscovery
          eyebrow="Service connectivity"
          heading={`Scaling your ${service.title} presence.`}
          intro={`Our ${service.title} framework is designed to integrate with localized market dominance and vertical-specific playbooks.`}
          links={[
            {
              to: "/services/seo/",
              anchor: "Core SEO & AI search services",
              note: "The technical and content engine that powers the industry playbook."
            },
            {
              to: "/industries/",
              anchor: "Vertical-specific search playbooks",
              note: "How we adapt our methodology for specific industry economics."
            },
            {
              to: `/seo-agency-${siteConfig.contact.address.city.toLowerCase()}/`,
              anchor: `${siteConfig.contact.address.city} HQ & local strategy`,
              note: "Where the team, the methodology, and the results originate."
            },
            {
              to: "/contact/",
              anchor: "Book a Strategy Call",
              note: "Discuss how to map this framework to your specific business goals."
            }
          ]}
        />
    </div>
  );
}
